/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file buildAsyncIgnorer internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\build-async-ignorer
 */

import { Ignorer } from "../../";
import { buildAsyncFinisher } from "./build-async-finisher";
import { AsyncFinishersRef, StateUpdaterRef } from "../types";

/**
 * Build a function that the caller can use to ignore executions.
 * @param asyncFinishers React ref to the current list of async finisher functions
 * @param updateState React ref to the current function used to update state
 */
export function buildAsyncIgnorer(asyncFinishers: AsyncFinishersRef, updateState: StateUpdaterRef): Ignorer {
	function ignorer(ids?: number | number[]): void {
		if (typeof ids === "number") {
			ids = [ids];
		} else if (typeof ids === "undefined") {
			ids = Object.keys(asyncFinishers.current).map(strKey => Number.parseInt(strKey, 10));
		}
		ids.forEach(id => {
			updateState.current({ type: "ignore", id });
			// Rebuild the async finisher for this execution such that it will no longer invoke the callbacks.
			asyncFinishers.current[id] = buildAsyncFinisher(id, updateState);
		});
	}
	return ignorer;
}
